import { translate } from '../utils/locale';
import { Context } from 'features/app/types';

export enum BenchmarkSetTypeId {
	NationalBenchmark = 1,
	CustomerBenchmark = 2,
	IndependentBenchmark = 3,
}

export enum BenchmarkSetTypeText {
	National = 1,
	Customer = 2,
	Independent = 3,
}

/**
 * Retrieves the string key from the BenchmarkSetTypeText enum that corresponds to a numeric value.
 * This function filters out computed enum values and returns the matching key.
 *
 * @param {number} value - The numeric value to look up in the BenchmarkSetTypeText enum
 * @returns {string} The matching enum key, or an empty string if no match is found
 *
 * @example
 * getBenchmarkSetKey(1) // returns 'National'
 * getBenchmarkSetKey(2) // returns 'Customer'
 * getBenchmarkSetKey(3) // returns 'Independent'
 */
export function getBenchmarkSetKey(value: number): string {
	const keys = Object.keys(BenchmarkSetTypeText).filter(
		(k) => !isNaN(Number(BenchmarkSetTypeText[k as keyof typeof BenchmarkSetTypeText]))
	);
	const key = keys.find(
		(k) => BenchmarkSetTypeText[k as keyof typeof BenchmarkSetTypeText] === value
	) as keyof typeof BenchmarkSetTypeText;
	return key || '';
}

export const benchmarkDisplayName = (value: number): string => {
	switch (value) {
		case BenchmarkSetTypeText.National:
			return translate('standardPage.benchmarkDisplayName.NATIONAL') as string;
		case BenchmarkSetTypeText.Customer:
			return translate('standardPage.benchmarkDisplayName.CUSTOMER') as string;
		case BenchmarkSetTypeText.Independent:
			return translate('standardPage.benchmarkDisplayName.INDEPENDENT') as string;
		default:
			return translate('standardPage.benchmarkDisplayName.NATIONAL') as string;
	}
};

/**
 * checkts to see if school has IndependentBenchmark BM as a valid option
 * @param arrayOfBenchmarkObjects
 * @returns true or false
 */
export const containsIndependentBenchmarkAsOption = (
	arrayOfBenchmarkObjects: Context.AvailableBenchmarks[]
) => {
	return arrayOfBenchmarkObjects.some(
		(benchmark) => benchmark.type === BenchmarkSetTypeId.IndependentBenchmark
	);
};

/**
 * Determines whether the customer benchmark should be hidden for the current client (US 52405).
 * Works like the `avaliable_benchmarks` config: the `hide_customer_benchmark` config holds the
 * benchmark-set countries (e.g. ["England", "Jersey"]) whose customer benchmark is hidden, and we
 * check the client's national benchmark-set country against that list. The independent benchmark
 * is never affected. Any country not listed (e.g. Wales) is left unchanged.
 *
 * @param hideCustomerBenchmarkCountries - the `hide_customer_benchmark` config value
 * @param benchmarkCountry - the client's benchmark-set country (from getBenchmarkSetCountryFromNationDataSetBenchmark)
 * @returns true when the customer benchmark should be hidden
 */
export const shouldHideCustomerBenchmark = (
	hideCustomerBenchmarkCountries: string[] | undefined,
	benchmarkCountry: string | null
): boolean => {
	if (!hideCustomerBenchmarkCountries || !benchmarkCountry) {
		return false;
	}
	return hideCustomerBenchmarkCountries.includes(benchmarkCountry);
};

/**
 * Removes the customer benchmark from a list of dropdown options (US 52405).
 * Options are matched to benchmark objects by their `value`/`benchmarkSet`, so
 * non-independent schools are left with National only and independent schools
 * with National + Independent.
 *
 * @param options - the benchmark dropdown options ({ value } matches benchmarkSet)
 * @param benchmarkObjects - the available benchmark objects (carry the `type`)
 * @returns the options with the customer benchmark removed
 */
export const removeCustomerBenchmarkOption = <T extends { value: string }>(
	options: T[] | undefined,
	benchmarkObjects: Context.AvailableBenchmarks[] | undefined
): T[] | undefined => {
	if (!options) {
		return options;
	}
	const customerBenchmarkSets = (benchmarkObjects ?? [])
		.filter((benchmark) => benchmark.type === BenchmarkSetTypeId.CustomerBenchmark)
		.map((benchmark) => benchmark.benchmarkSet);
	return options.filter((option) => !customerBenchmarkSets.includes(option.value));
};
